import {Injectable} from '@angular/core';
import {
  ActionPerformed,
  Channel,
  PermissionStatus,
  PushNotifications,
  PushNotificationSchema,
  Token
} from '@capacitor/push-notifications';
import {Observable} from 'rxjs';
import {retry} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {MenuController, NavController, Platform, ToastController} from '@ionic/angular';
import {Router} from '@angular/router';
import {TranslationService} from '../translation';
import {IAuthData} from '../models/IAuthData';
import {saveToLocalStorage} from '../store/actions/store.actions';
import {authorize, setLocalUser} from '../store/actions/user.actions';
import {Store} from '@ngrx/store';
import {IAppState} from '../models/IAppState';
import {AuthWithPushNotificationService} from './auth-with-push-notification.service';
import {CheckinType} from '../models/ICheckin';
import {IUserData, MemberType} from '../models/IUserData';
import {SoundService} from './sound.service';
import {setDeviceTokenAndOperatingSystem} from '../store/actions/device.actions';
import {IDeviceInfo} from '../models/IDeviceInfo';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/messaging';
import {DeviceService} from './device.service';

export enum PushTypes {
  CheckinAccepted = 'checkin-accepted',
  CheckinCheckedOut = 'checkin-checked-out',
  CheckinQueued = 'checkin-queued',
  CheckinRejected = 'checkin-rejected',
  PresenceConfirmationRequest = 'presence-confirmation-request',
  DequeueConfirmationRequest = 'dequeue-confirmation-request',
  RegistrationConfirmed = 'registration-confirmed',
  CheckinChatMessage = 'checkin-chat-message',
  LockoutAlert = 'lockout-alert',
  NewReservation = 'new-reservation',
  CancelReservation = 'reservation-cancelled'
}

export interface IPushPayload {
  checkinType?: CheckinType;
  type?: PushTypes;
  reason?: string;
  checkinId?: string;
  reservationId?: string;
  coldstart?: boolean;
  phoneNumber?: string;
  dialCode?: string;
  foreground?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private static readonly DEVICE_REGISTRATION_RETRY_DELAY_MS = 20000;
  private static readonly DEVICE_REGISTRATION_RETRY_COUNT = 5;
  private static readonly DEFAULT_ANDROID_CHANNEL_ID = 'safe_checkin_default';
  private static readonly DEFAULT_ANDROID_CHANNEL_NAME = 'General Notifications';

  deviceRegistrationId: string;
  private deviceUuid: string;
  private state$: Observable<IAppState> = this.store.select((state) => state.AppStateReducer);
  private userData: IUserData;
  private isAuthorised = false;
  private tokenRegistered = false;
  private nativeListenersRegistered = false;
  private nativeListenersRegistrationPromise: Promise<void> | null = null;
  private backendRegisteredToken: string | null = null;
  private backendRegistrationInFlight = false;
  private defaultAndroidChannelCreated = false;
  private defaultAndroidChannelPromise: Promise<void> | null = null;
  private isAppInBackground = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private navCtrl: NavController,
    private toastController: ToastController,
    private deviceService: DeviceService,
    private platform: Platform,
    private translateService: TranslationService,
    private ionMenu: MenuController,
    private soundService: SoundService,
    private store: Store<{AppStateReducer: IAppState}>,
    private authWithPushService: AuthWithPushNotificationService
  ) {
    this.state$.subscribe((appState: IAppState) => {
      this.userData = appState.userData;
      this.isAuthorised = !!appState.isAuthorised;
      if (!this.isAuthorised) {
        this.backendRegisteredToken = null;
        return;
      }
    });
    this.deviceService.getUuid().then((uuid) => (this.deviceUuid = uuid));

    this.platform.resume.subscribe(async () => {
      this.isAppInBackground = false;
      await this.clearPushDeliveredNotifications();
      await this.tryRegisterDeviceWithBackend();
    });

    this.platform.pause.subscribe(() => {
      this.isAppInBackground = true
    });
  }

  async registerPushService() {
    if (!this.platform.is('hybrid')) {
      console.log('[push] registerPushService skipped: not hybrid platform');
      return;
    }

    console.log('[push] registerPushService start', {
      isAndroid: this.platform.is('android'),
      isIos: this.platform.is('ios')
    });

    await this.ensureNativeListeners();
    const granted = await this.ensurePushPermission();
    if (!granted) {
      console.warn('[push] registerPushService aborted: permission not granted');
      return;
    }

    await this.ensureDefaultAndroidChannel();
    await PushNotifications.register();
    console.log('[push] PushNotifications.register() called');
    await this.tryRegisterDeviceWithBackend();
  }

  async getDeviceRegistrationIdAndOperatingSystem() {
    if (!this.platform.is('hybrid')) {
      console.log('[push] getDeviceRegistrationIdAndOperatingSystem skipped: not hybrid platform');
      return;
    }

    await this.ensureNativeListeners();
    const granted = await this.ensurePushPermission();
    if (!granted) {
      console.warn('[push] getDeviceRegistrationIdAndOperatingSystem aborted: permission not granted');
      return;
    }

    await this.ensureDefaultAndroidChannel();
    await PushNotifications.register();
    console.log('[push] PushNotifications.register() called from getDeviceRegistrationIdAndOperatingSystem');
  }

  async initiatePush() {
    await this.registerPushService();
  }

  private async ensureNativeListeners() {
    if (this.nativeListenersRegistered) {
      return;
    }
    if (this.nativeListenersRegistrationPromise !== null) {
      await this.nativeListenersRegistrationPromise;
      return;
    }

    this.nativeListenersRegistrationPromise = (async () => {
      console.log('[push] Registering native push listeners');

      await Promise.all([
        PushNotifications.addListener('registration', async (token: Token) => {
          console.log('[push] registration token received', {
            tokenLength: token?.value?.length || 0
          });
          const operatingSystem = this.platform.is('android') ? 'android' : 'ios';
          this.setDeviceTokenAndOperatingSystem(token.value, operatingSystem);
          await this.tryRegisterDeviceWithBackend();
        }),
        PushNotifications.addListener('registrationError', (error: any) => {
          console.error('[push] registrationError', JSON.stringify(error));
        }),
        PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
          this.handleNativeNotification(notification, true);
        }),
        PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
          this.handleNativeNotification(notification.notification, false);
        })
      ]);

      this.nativeListenersRegistered = true;
      console.log('[push] Native push listeners registered');
    })();

    try {
      await this.nativeListenersRegistrationPromise;
    } finally {
      this.nativeListenersRegistrationPromise = null;
    }
  }

  private async ensurePushPermission(): Promise<boolean> {
    let permissionStatus: PermissionStatus = await PushNotifications.checkPermissions();
    console.log('[push] checkPermissions()', permissionStatus);

    if (permissionStatus.receive !== 'granted') {
      permissionStatus = await PushNotifications.requestPermissions();
      console.log('[push] requestPermissions()', permissionStatus);
    }

    if (permissionStatus.receive !== 'granted') {
      console.warn('Push notification permission not granted');
      return false;
    }

    return true;
  }

  private async ensureDefaultAndroidChannel(): Promise<void> {
    if (!this.platform.is('android')) {
      return;
    }

    if (this.defaultAndroidChannelCreated) {
      return;
    }

    if (this.defaultAndroidChannelPromise !== null) {
      await this.defaultAndroidChannelPromise;
      return;
    }

    this.defaultAndroidChannelPromise = (async () => {
      const channel: Channel = {
        id: PushNotificationService.DEFAULT_ANDROID_CHANNEL_ID,
        name: PushNotificationService.DEFAULT_ANDROID_CHANNEL_NAME,
        description: 'Default channel for push notifications',
        importance: 5,
        visibility: 1
      };

      try {
        await PushNotifications.createChannel(channel);
        const channels = await PushNotifications.listChannels();
        this.defaultAndroidChannelCreated = true;
        console.log('[push] Android notification channel ready', {
          channelId: channel.id,
          channels: channels?.channels?.map((c) => c.id) || []
        });
      } catch (error) {
        console.warn('[push] Failed to create/list Android notification channel', error);
      }
    })();

    try {
      await this.defaultAndroidChannelPromise;
    } finally {
      this.defaultAndroidChannelPromise = null;
    }
  }

  private async tryRegisterDeviceWithBackend(): Promise<void> {
    if (!this.platform.is('hybrid')) {
      console.log('[push] tryRegisterDeviceWithBackend skipped: not hybrid');
      return;
    }

    if (!this.isAuthorised) {
      console.log('[push] tryRegisterDeviceWithBackend skipped: not authorised');
      return;
    }

    const token = this.deviceRegistrationId;
    if (!token) {
      console.log('[push] tryRegisterDeviceWithBackend skipped: no device token yet');
      return;
    }

    if (this.backendRegisteredToken === token) {
      console.log('[push] tryRegisterDeviceWithBackend skipped: token already registered');
      return;
    }

    if (this.backendRegistrationInFlight) {
      console.log('[push] tryRegisterDeviceWithBackend skipped: registration in flight');
      return;
    }

    this.backendRegistrationInFlight = true;

    try {
      const operatingSystem = this.platform.is('android') ? 'android' : 'ios';
      await this.registerDevice({
        registrationId: token,
        registrationType: operatingSystem === 'android' ? 'FCM' : 'APNS'
      }).toPromise();
      this.backendRegisteredToken = token;
      console.log('[push] Device registered on backend', {
        operatingSystem,
        tokenLength: token.length
      });
    } catch (e) {
      console.warn('Failed to register device for push notifications', e);
    } finally {
      this.backendRegistrationInFlight = false;
    }
  }

  private handleNativeNotification(notification: PushNotificationSchema, foreground: boolean) {
    console.log('[push] notification', JSON.stringify(notification, null, 2));
    const payload = this.extractPushPayload(notification);
    console.log('[push] payload', payload);
    const message = notification.body || payload.body || payload.message || '';
    const title = notification.title || payload.title || '';
    const coldstart = !foreground || !!payload.coldstart;



    console.log('[push] Native notification received', {
      foreground,
      coldstart,
      type: payload?.type,
      checkinId: payload?.checkinId,
      reservationId: payload?.reservationId,
      title,
      message,
      hasData: !!notification?.data
    });

    this.proceedPush(payload, message, title, {coldstart, foreground});
  }

  private extractPushPayload(notification: PushNotificationSchema): IPushPayload & {title?: string; body?: string; message?: string} {
    const rawData = ((notification?.data || {}) as any);
    const apsData = this.parsePushPayloadValue(rawData.aps);
    const apsAlertData = this.parsePushPayloadValue((apsData as any)?.alert);
    const nestedData = this.parsePushPayloadValue(rawData.data);

    return {
      ...rawData,
      ...(apsData && typeof apsData === 'object' ? apsData : {}),
      ...(apsAlertData && typeof apsAlertData === 'object' ? apsAlertData : {}),
      ...(nestedData && typeof nestedData === 'object' ? nestedData : {})
    };
  }

  private parsePushPayloadValue(value: unknown): Partial<IPushPayload> | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'object') {
      return value as Partial<IPushPayload>;
    }

    if (typeof value !== 'string') {
      return null;
    }

    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  registerDevice(registration: {registrationId: string; registrationType?: string}): Observable<any> {
    console.log('registration', registration);
    let operatingSystem: string;

    if (registration.registrationType === 'FCM') {
      operatingSystem = 'android';
    } else if (registration.registrationType === 'web') {
      operatingSystem = 'web';
    } else {
      operatingSystem = 'ios';
    }

    return this.http.post<any>(`${environment.apiUrl}/me/devices`, {
      operatingSystem,
      token: registration.registrationId
    }, {
      headers: {
        errors: 'silent'
      }
    }).pipe(
      retry({
        count: PushNotificationService.DEVICE_REGISTRATION_RETRY_COUNT,
        delay: PushNotificationService.DEVICE_REGISTRATION_RETRY_DELAY_MS
      })
    );
  }

  removeDevice(): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}/me/devices/${this.deviceRegistrationId}`);
  }

  async clearPushDeliveredNotifications(): Promise<void> {
    if (this.platform.is('hybrid') && !this.isAppInBackground) {
      try {
        await PushNotifications.removeAllDeliveredNotifications();
      } catch (e) {
        console.warn('Failed to clear delivered notifications', e);
      }
    }
  }

  async proceedPush(payload: IPushPayload, message?: string, title?: string, context?: {coldstart?: boolean; foreground?: boolean}) {
    const coldstart = context?.coldstart ?? payload.coldstart ?? false;
    const foreground = context?.foreground ?? payload.foreground ?? false;
    const type = payload.type;

    this.closeMenuIfNeeded(coldstart, foreground);

    const handlers: Partial<Record<PushTypes, () => Promise<void>>> = {
      [PushTypes.RegistrationConfirmed]: async () => this.handleRegistrationConfirmed(payload),
      [PushTypes.NewReservation]: async () => this.handleNewReservation(payload, message, title, {coldstart, foreground}),
      [PushTypes.CancelReservation]: async () => this.handleCancelReservation(payload, message, title, {coldstart, foreground}),
      [PushTypes.CheckinChatMessage]: async () => this.handleChatMessage(payload, message, title, {coldstart, foreground}),
      [PushTypes.LockoutAlert]: async () => this.handleLockout(message, title, foreground)
    };

    const handler = type ? handlers[type] : undefined;
    if (handler) {
      await handler();
      return;
    }

    await this.handleDefaultNotification({...payload, coldstart, foreground}, message, title);
  }

  async showPushNotification(payload: IPushPayload, message: string, header?: string) {
    const toast = await this.toastController.create({
      message: message,
      header: header || undefined,
      duration: 4000,
      position: 'top',
      buttons: [{
        text: this.translateService.instant('Ok'),
        role: 'cancel',
        handler: () => {
          this.navigateToPushTarget(payload);
          toast.dismiss();
        }
      }]
    });
    toast.present();
  }

  async showChatPushNotification(payload: IPushPayload, message: string, header?: string) {
    const toast = await this.toastController.create({
      message: message,
      header: header || undefined,
      duration: 4000,
      position: 'top',
      buttons: [{
        text: this.translateService.instant('Read'),
        role: 'cancel',
        handler: () => {
          if (payload.checkinId) {
            this.navCtrl.navigateForward(['/chat/' + payload.checkinId]);
          }
          toast.dismiss();
        }
      }]
    });
    toast.present();
  }

  async showReservationPushNotification(payload: IPushPayload, message: string, header?: string) {
    if (payload.coldstart || !payload.foreground) {
      this.navigateToPushTarget(payload);

      return;
    }

    const toast = await this.toastController.create({
      message: message,
      header: header || undefined,
      duration: 4000,
      position: 'top',
      buttons: [{
        text: this.translateService.instant('Open'),
        role: 'cancel',
        handler: () => {
          this.navigateToPushTarget(payload);
          toast.dismiss();
        }
      }]
    });
    toast.present();
  }

  async showCancelReservationPushNotification(payload: IPushPayload, message: string, header?: string) {
    if (payload.coldstart || !payload.foreground) {
      this.navigateToPushTarget(payload);

      return;
    }

    const toast = await this.toastController.create({
      message: message,
      header: header || undefined,
      duration: 4000,
      position: 'top',
      buttons: [{
        text: this.translateService.instant('Open'),
        role: 'cancel',
        handler: () => {
          this.navigateToPushTarget(payload);
          toast.dismiss();
        }
      }]
    });
    toast.present();
  }

  private closeMenuIfNeeded(coldstart: boolean, foreground: boolean) {
    if (coldstart || !foreground) {
      this.ionMenu.close();
    }
  }

  private navigateToPushTarget(payload: IPushPayload): boolean {
    const targetUrl = this.getPushTargetUrl(payload);
    if (!targetUrl) {
      return false;
    }

    this.navCtrl.navigateForward([targetUrl]);
    return true;
  }

  private getPushTargetUrl(payload: IPushPayload): string | null {
    if (!payload) {
      return null;
    }

    if (payload.type === PushTypes.CheckinChatMessage && payload.checkinId) {
      return `/chat/${payload.checkinId}`;
    }

    if (payload.reservationId) {
      return `/reservation/${payload.reservationId}`;
    }

    if (!payload.checkinId) {
      return payload.type === PushTypes.CancelReservation ? '/reservations-list' : null;
    }

    return this.getCheckinPushTargetUrl(payload);
  }

  private getCheckinPushTargetUrl(payload: IPushPayload): string {
    const checkinType = this.normalizeCheckinType(payload.checkinType);

    if (checkinType === CheckinType.ticket) {
      return `/check-in-waiting/${payload.checkinId}`;
    }

    return `/check-in-registration/${payload.checkinId}`;
  }

  private normalizeCheckinType(checkinType?: CheckinType): CheckinType | null {
    if (checkinType === CheckinType.ticket || checkinType === CheckinType.registration) {
      return checkinType;
    }

    if (typeof checkinType !== 'string') {
      return null;
    }

    if (checkinType === CheckinType.ticket) {
      return CheckinType.ticket;
    }

    if (checkinType === CheckinType.registration) {
      return CheckinType.registration;
    }

    return null;
  }

  private async handleRegistrationConfirmed(payload: IPushPayload) {
    await this.checkForRegistrationRequestStatusAndLogin(payload.dialCode, payload.phoneNumber);
  }

  private async handleNewReservation(
    payload: IPushPayload,
    message?: string,
    title?: string,
    context?: {coldstart?: boolean; foreground?: boolean}
  ) {
    this.soundService.playLoud();
    await this.showReservationPushNotification({...payload, ...context}, message, title);
  }

  private async handleCancelReservation(
    payload: IPushPayload,
    message?: string,
    title?: string,
    context?: {coldstart?: boolean; foreground?: boolean}
  ) {
    this.soundService.playLoud();
    await this.showCancelReservationPushNotification({...payload, ...context}, message, title);
  }

  private async handleChatMessage(
    payload: IPushPayload,
    message?: string,
    title?: string,
    context?: {coldstart?: boolean; foreground?: boolean}
  ) {
    const coldstart = context?.coldstart ?? payload.coldstart ?? false;
    const foreground = context?.foreground ?? payload.foreground ?? false;
    const isCheckinOpened = !!payload.checkinId && window.location.href.includes(payload.checkinId);
    const isChatOpened = !!payload.checkinId && window.location.href.includes('chat/' + payload.checkinId);
    const isCheckinsList = window.location.href.includes('checkin-list');
    const isCompanyMember = this.userData?.memberType === MemberType.companyMember;
    const shouldNavigate = coldstart || isCheckinOpened || (!isCompanyMember && isCheckinsList) || !foreground;

    if (!coldstart && !isChatOpened) {
      this.soundService.playLoud();
    }

    if (shouldNavigate) {
      this.navigateToPushTarget(payload);
      return;
    }

    await this.showChatPushNotification(payload, message, title);
  }

  private async handleLockout(message?: string, title?: string, foreground?: boolean) {
    if (foreground) {
      await this.showLockoutPushNotification(message, title);
    }
    this.authWithPushService.logout(!!foreground);
  }

  private async handleDefaultNotification(payload: IPushPayload, message?: string, title?: string) {
    const isCheckinOpened = !!payload.checkinId && window.location.href.includes(payload.checkinId);
    const isCompanyMember = this.userData?.memberType === MemberType.companyMember;

    if (isCheckinOpened || isCompanyMember) {
      return;
    }

    const hideNote = payload.type === PushTypes.CheckinAccepted || payload.type === PushTypes.CheckinCheckedOut;
    let msg = message || '';

    if (payload.reason) {
      msg += ` ${payload.reason}`;
    }

    if (hideNote || payload.coldstart || !payload.foreground) {
      this.navigateToPushTarget(payload);
      return;
    }

    if (msg) {
      await this.showPushNotification(payload, msg, title);
    }
  }

  async checkForRegistrationRequestStatusAndLogin(dialCode: string, phoneNumber: string) {
    try {
      const phone = dialCode.replace('+', '') + phoneNumber;
      const registrationRequestStatus = await this.authWithPushService.isRegistrationRequestConfirmed(phone, this.deviceUuid).toPromise();

      if (registrationRequestStatus) {
        await this.logInByPhoneAndDeviceUuid(this.deviceUuid, dialCode, phoneNumber);
      } else {
        const toast = await this.toastController.create({
          message: this.translateService.instant('unconfirmed_registration_request'),
          position: 'top',
          color: 'danger',
          duration: 3000
        });
        toast.present();
        return;
      }
    } catch (e) {
      return;
    }
  }

  async logInByPhoneAndDeviceUuid(deviceUuid: string, dialCode: string, phoneNumber: string) {
    let authData: IAuthData;
    try {
      const phone = dialCode.replace('+', '') + phoneNumber;
      authData = await this.authWithPushService.loginWithPhoneNumberAndDeviceUuid(phone, deviceUuid).toPromise();
    } catch (e) {
      return;
    }

    this.store.dispatch(authorize({isAuthorised: true, authData}));
    this.store.dispatch(
      setLocalUser({
        localUser: {
          phoneCode: dialCode,
          phone: phoneNumber
        }
      })
    );
    this.store.dispatch(saveToLocalStorage());
    this.router.navigate(['/setup-profile'], {replaceUrl: true});
  }

  async showLockoutPushNotification(message: string, header?: string) {
    const toast = await this.toastController.create({
      message: message,
      header: header || undefined,
      duration: 4000,
      position: 'top'
    });

    toast.onDidDismiss().then(() => {
      if (this.router.url !== '/login') {
        window.location.pathname = '/';
      }
    });

    toast.present();
  }

  setDeviceTokenAndOperatingSystem(deviceToken: string, operatingSystem: string) {
    this.deviceRegistrationId = deviceToken;
    this.store.dispatch(setDeviceTokenAndOperatingSystem({
      deviceInfo: {
        deviceToken: deviceToken,
        deviceOperatingSystem: operatingSystem
      } as IDeviceInfo
    }));
    void this.tryRegisterDeviceWithBackend();
  }

  async initiateWebPush() {
    const isWeb = this.platform.is('mobileweb') || this.platform.is('desktop');
    if (!isWeb) {
      // Skip web push setup on native (Capacitor/Cordova) where FCM/APNs is used instead.
      return;
    }

    if ('serviceWorker' in navigator ) {
      try {
        const registration = await navigator.serviceWorker.register('firebase-messaging-sw.js');
        console.log('Service Worker registered with scope:', registration.scope);
      } catch (err) {
        console.log('Service Worker registration failed:', err);
      }
    }

    try {
      const currentToken = await this.requestWebToken();
      if (currentToken) {
        this.tokenRegistered = true;
        await this.registerDevice({registrationId: currentToken, registrationType: 'web' }).toPromise();
        this.backendRegisteredToken = currentToken;
      } else {
        console.log('No registration token available. Request permission to generate one.');
      }
    } catch (err) {
      console.log('An error occurred while retrieving token. ', err);
    }
  }

  async requestWebToken(): Promise<string> {
    try {
      const app = (document as any).fbApp;
      const appMessaging = firebase.messaging(app);
      return await appMessaging.getToken({
        vapidKey: environment.vapidKey
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  }
}
